import { FC } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Heading,
  Text,
  VStack,
} from "@chakra-ui/react";
import Link from "next/link";
import { ScrollReveal } from "@/components/ScrollReveal";

interface DemoCard {
  id: string;
  name: string;
  description: string;
  industry: string;
  orgId: string;
  accentColor: string;
}

const DEMOS: DemoCard[] = [
  {
    id: "siam-wellness",
    name: "Siam Wellness & Spa",
    description: "Thajský maserský salón s elegantným a relaxačným dizajnom",
    industry: "Wellness & Spa",
    orgId: "635cc45c-d7c2-43c8-a2ea-44693bdded39",
    accentColor: "pink.500",
  },
  {
    id: "fitzone",
    name: "FitZone",
    description: "Moderné fitness štúdio s personálnymi trénermi a energickým dizajnom",
    industry: "Fitness & Health",
    orgId: "a1b2c3d4-e5f6-4a5b-8c9d-0e1f2a3b4c5d",
    accentColor: "cyan.500",
  },
  {
    id: "fitcentrumsport",
    name: "Another Fitness Studio",
    description: "Veľké fitness štúdio s viac ako 10 trénermi a širokým výberom tréningov",
    industry: "Fitness & Sport",
    orgId: "f1a2c3e4-d5e6-47f8-a9b0-c1d2e3f4a5b6",
    accentColor: "red.500",
  },
  {
    id: "lens-and-light",
    name: "Lens & Light Photography",
    description: "Profesionálne fotografické štúdio s minimalistickým dizajnom a inline rezerváciami",
    industry: "Photography",
    orgId: "fbc9a0b4-2d7e-437c-867d-2183a0d1aef9",
    accentColor: "yellow.600",
  },
];

interface LiveDemosProps {
  locale?: string;
}

export const LiveDemos: FC<LiveDemosProps> = ({ locale = 'sk' }) => (
  <Box id="demo" py={{ base: "16", md: "24" }} bg="gray.50" position="relative">
    <Container maxW="7xl">
      <VStack spacing="12">
        {/* Section header */}
        <VStack spacing="4" textAlign="center" maxW="800px" mx="auto">
          <Heading as="h2" textStyle="heading36" color="gray.900">
            Vyskúšajte{" "}
            <Text
              as="span"
              bgGradient="linear(to-r, primary, pink.400)"
              bgClip="text"
            >
              živé demo
            </Text>
          </Heading>
          <Text textStyle="heading20" color="gray.600" lineHeight="1.7">
            Pozrite si, ako vyzerajú widgety prispôsobené rôznym odvetviam
          </Text>
        </VStack>

        {/* Demo cards */}
        <Grid
          templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
          gap="8"
          w="100%"
        >
          {DEMOS.map((demo, index) => (
            <ScrollReveal key={demo.id} delay={index * 100}>
              <Box
                bg="white"
                borderRadius="2xl"
                p="8"
                boxShadow="lg"
                border="1px solid"
                borderColor="gray.200"
                transition="all 0.3s"
                _hover={{
                  transform: "translateY(-4px)",
                  boxShadow: "2xl",
                  borderColor: demo.accentColor,
                }}
                h="100%"
                display="flex"
                flexDirection="column"
              >
                <VStack align="stretch" spacing="4" flex="1">
                  {/* Industry badge */}
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="700"
                      textTransform="uppercase"
                      letterSpacing="wider"
                      color={demo.accentColor}
                      bg={`${demo.accentColor.split('.')[0]}.50`}
                      px="3"
                      py="1"
                      borderRadius="full"
                      display="inline-block"
                    >
                      {demo.industry}
                    </Text>
                  </Box>

                  {/* Title and description */}
                  <VStack align="stretch" spacing="2" flex="1">
                    <Heading as="h3" fontSize="2xl" color="gray.900" fontWeight="700">
                      {demo.name}
                    </Heading>
                    <Text color="gray.600" fontSize="md" lineHeight="1.6">
                      {demo.description}
                    </Text>
                  </VStack>

                  {/* CTA Button */}
                  <Box pt="4">
                    <Link href={`/p/${demo.orgId}?lang=${locale}`} passHref legacyBehavior>
                      <Button
                        as="a"
                        size="lg"
                        w="100%"
                        bgGradient={`linear(to-r, ${demo.accentColor}, ${demo.accentColor.replace('500', '600')})`}
                        color="white"
                        _hover={{
                          transform: "scale(1.02)",
                          boxShadow: "lg",
                        }}
                        transition="all 0.2s"
                        fontWeight="700"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Otvoriť demo
                      </Button>
                    </Link>
                  </Box>
                </VStack>
              </Box>
            </ScrollReveal>
          ))}
        </Grid>

        {/* Bottom CTA */}
        <Box textAlign="center" pt="8">
          <Text color="gray.600" fontSize="md">
            Každé demo má úplne iný dizajn prispôsobený značke klienta
          </Text>
        </Box>
      </VStack>
    </Container>
  </Box>
);
